import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form } from 'semantic-ui-react';
import { useEffect, useState } from 'react';
import { Check, Info } from '@phosphor-icons/react';
import {
  CenterDropdownStyle,
  ControlledTextAreaFormField,
  ControlledTextFormField,
  DangerText,
  DateFormField,
  DropdownFormField,
  HoverBorderButton,
  MuteText,
  StyledButton,
} from '../../elements';
import { DarkPopup, SkupremeModal } from '../../components';
import { SkuSearchInput } from './skuSearch';
import { CustomerSearchInput } from '../crm/CustomerSearch';
import { CommonUtility, InventoryService, theme, ToastMessage } from '../../utility';
import { useLotAttributesHook } from '../../hooks';
import { useAuth } from '../../contexts/auth';
import { InventoryLocationSearch } from './LocationSearch';
import { useGlobalContext } from '../../contexts';

const ItemSchema = yup.object().shape({
  warehouseId: yup.string().required('Warehouse is required'),
  expiryDate: yup.string().optional(),
  lotNumber: yup.string().optional(),
  palletNumber: yup.string(),
  note: yup.string().optional(),
  quantity: yup
    .number()
    .typeError('Quantity is required')
    .transform((_, val) => (!isNaN(val) ? Number(val) : null))
    .required('Quantity is required')
    .integer('Quantity must be an integer')
    .min(1, 'Quantity must be at least 1'),
});

export function AddInventoryPopup({ open, onClose, refresh, defaultWarehoueId }) {
  const [formValues, setFormValues] = useState({
    customer: null,
    sku: null,
    location: null,
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(ItemSchema),
    shouldUnregister: true,
    mode: 'onChange',
  });

  const [loading, setLoading] = useState(false);
  const [aliasName, setAliasName] = useState('');
  const [aliasOptions, setAliasOptions] = useState([]);
  const [fieldsError, setFieldsError] = useState({
    alias: '',
    sku: '',
    location: '',
  });

  const warehouseId = watch('warehouseId');

  const { user, currentOrg } = useAuth();

  const { data, loading: lotAttributesLoading } = useLotAttributesHook(
    formValues?.sku?._id,
  );

  const { warehouseOptions, warehouseLoading } = useGlobalContext();

  const resetFields = () => {
    setFormValues({
      location: null,
      sku: null,
      customer: null,
    });

    setFieldsError({
      alias: '',
      sku: '',
      location: '',
    });

    setAliasName('');
    setAliasOptions([]);
  };

  useEffect(() => {
    if (open && defaultWarehoueId) {
      setValue('warehouseId', defaultWarehoueId);
      resetFields();
    }
  }, [open, defaultWarehoueId]);

  useEffect(() => {
    // Clear SKU error if SKU is selected
    if (formValues.sku) {
      createFieldError('sku', '');
    }
  }, [formValues.sku]);

  useEffect(() => {
    // Clear Location error if Location is selected
    if (formValues.location) {
      createFieldError('location', '');
    }
  }, [formValues.location]);

  const createFieldError = (key, value) => {
    setFieldsError(fieldsError => ({ ...fieldsError, [key]: value }));
  };

  const onSubmit = async values => {
    if (!formValues.sku) {
      createFieldError('sku', 'Product SKU is required');
      return;
    }

    if (!formValues.location) {
      createFieldError('location', 'Location is required');
      return;
    }

    if (!aliasName) {
      createFieldError('alias', 'Alias is required');
      return;
    }

    const locationToUse = formValues.location._id;

    let payload = {
      organizationId: currentOrg?._id,
      customerOrganizationId: formValues.customer?._id,
      locationId: locationToUse,
      warehouseId,
      userId: user?._id,
      note: values.note,
      items: [
        {
          productId: formValues.sku._id,
          inventoryType: 'case',
          alias: aliasName,
          lotNumber: values.lotNumber,
          palletNumber: values.palletNumber,
          expiryDate: values.expiryDate,
          quantity: {
            total: values.quantity,
            available: values.quantity,
            uom: 'ea',
          },
        },
      ],
    };

    payload = CommonUtility.removeEmptyFields(payload);

    try {
      setLoading(true);
      await InventoryService.checkInInventory(payload);
      ToastMessage.success('Inventory added successfully');
      onClose();
      reset();
      resetFields();
      if (CommonUtility.isFunction(refresh)) {
        refresh();
      }
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const options =
      data?.cartonAttributes?.map(attribute => ({
        text: attribute.alias,
        value: attribute.alias,
        key: attribute.alias,
        item: attribute,
      })) || [];

    setAliasOptions(options);
  }, [data]);

  const onAddNewAlias = (e, { value: newAliasValue }) => {
    setAliasOptions(options => [
      ...options,
      { text: newAliasValue, value: newAliasValue, key: newAliasValue },
    ]);
  };

  const onChangeAlias = (e, data) => {
    setAliasName(data.value);
    createFieldError('alias', '');
  };

  return (
    <SkupremeModal open={open} size="mini" title="Add Inventory" onClose={onClose}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <MuteText>Customer</MuteText>
          <CustomerSearchInput
            onResultClick={customer => setFormValues({ ...formValues, customer })}
            inputClassName="mb-2"
            style={{ width: '100%' }}
          />
        </div>
        <DropdownFormField
          control={control}
          name="warehouseId"
          label="Warehouse"
          options={warehouseOptions}
          loading={warehouseLoading}
          fieldClass="mb-0"
        />
        <div className="mt-2">
          <div className="d-flex align-items-end">
            <MuteText>SKU</MuteText>
            {!formValues.customer?._id && (
              <DarkPopup
                content="Please select a customer to search SKU"
                trigger={<Info className="mb-1 ml-1" color={theme.colors.darkBlue} />}
              >
                Please select a customer to search SKU
              </DarkPopup>
            )}
          </div>
          <SkuSearchInput
            disabled={!formValues.customer?._id}
            onResultClick={sku => setFormValues({ ...formValues, sku })}
            organization_id={formValues.customer?._id}
          />
          {fieldsError.sku && <DangerText className="mt-1">{fieldsError.sku}</DangerText>}
        </div>
        <div className="my-2">
          <MuteText>Carton Configuration Name</MuteText>
          <CenterDropdownStyle
            className="mute-dropdown"
            options={aliasOptions}
            value={aliasName}
            onChange={onChangeAlias}
            selection
            search
            allowAdditions
            error={fieldsError.alias}
            placeholder="Type to add new alias"
            onAddItem={onAddNewAlias}
            loading={lotAttributesLoading}
          />
          {fieldsError.alias && <DangerText>{fieldsError.alias}</DangerText>}
        </div>
        <div className="my-2">
          <MuteText>Inventory Location</MuteText>
          <InventoryLocationSearch
            warehouseId={warehouseId}
            onResultClick={location => setFormValues({ ...formValues, location })}
          />
          {fieldsError.location && <DangerText>{fieldsError.location}</DangerText>}
        </div>
        <ControlledTextFormField
          control={control}
          name="lotNumber"
          label="Lot Number"
          placeholder="#Lot-341"
          error={errors?.lotNumber}
          hint={errors?.lotNumber?.message}
        />
        <ControlledTextFormField
          control={control}
          name="palletNumber"
          label="Pallet Number"
          error={errors?.palletNumber}
          hint={errors?.palletNumber?.message}
        />
        <ControlledTextFormField
          control={control}
          name="quantity"
          label="Quantity"
          placeholder="Quantity"
          error={errors?.quantity}
          hint={errors?.quantity?.message}
        />
        <DateFormField
          control={control}
          name="expiryDate"
          label="Expiry Date"
          placeholder="Expiry Date"
          error={errors?.expiryDate}
          hint={errors?.expiryDate?.message}
        />
        <ControlledTextAreaFormField
          control={control}
          name="note"
          label="Note"
          placeholder="Additional Note"
          error={errors?.note}
          hint={errors?.note?.message}
          rows="3"
        />
        <div className="d-flex justify-content-end mt-4">
          <HoverBorderButton onClick={onClose} disabled={loading} type="button">
            Cancel
          </HoverBorderButton>
          <StyledButton loading={loading} type="submit" className="ml-2">
            Confirm
          </StyledButton>
        </div>
      </Form>
    </SkupremeModal>
  );
}
